import { useMemo } from 'react';
import type { SavedHighscore } from '@/types/game';
import { computeRecords } from '@/utils/records';
import { RankRow } from './RankRow';

interface RecordsSectionProps {
  savedHighscores: SavedHighscore[];
}

export const RecordsSection = ({ savedHighscores }: RecordsSectionProps) => {
  const data = useMemo(() => computeRecords(savedHighscores), [savedHighscores]);

  const title = data.gamesAnalyzed === 0
    ? 'Rekorde'
    : data.dayIsToday
      ? 'Rekorde von heute'
      : `Rekorde vom ${data.dayLabel}`;

  const scope = `Aus ${data.gamesAnalyzed} ${data.gamesAnalyzed === 1 ? 'Spiel' : 'Spielen'} an diesem Tag · Spieler werden am Namen erkannt`;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{scope}</p>
      </div>

      {data.gamesAnalyzed === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-3.5">
          <p className="text-sm text-muted-foreground">
            Es liegen noch keine Spiele im Archiv. Sobald das erste Spiel gespeichert ist,
            erscheint hier die Auswertung.
          </p>
        </div>
      ) : (
        <>
          {data.records.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {data.records.map((record) => (
                <div
                  key={record.key}
                  className="rounded-2xl border border-border bg-card p-3.5"
                >
                  <div className="text-overline text-muted-foreground">{record.label}</div>
                  <div
                    className={`mt-1 text-2xl font-black leading-none tracking-[-0.02em] ${
                      record.key === 'score' ? 'text-rank-gold' : 'text-foreground'
                    }`}
                  >
                    {record.value}
                  </div>
                  <div className="mt-1.5 break-words text-xs text-muted-foreground">
                    {record.holder}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[
                      record.game.gameNumber ? `Spiel #${record.game.gameNumber}` : '',
                      record.game.date,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {record.note && (
                    <div className="mt-1 text-[11px] text-muted-foreground">{record.note}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {data.topScores.length > 0 && (
            <section>
              <h3 className="text-base font-bold">Top Ergebnisse</h3>

              <div className="mt-3 space-y-2">
                {data.topScores.map((entry, index) => (
                  <RankRow
                    key={entry.key}
                    rank={index + 1}
                    title={entry.name}
                    subtitle={[
                      entry.game.gameNumber ? `Spiel #${entry.game.gameNumber}` : '',
                      entry.game.date,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    value={entry.score.toLocaleString('de-DE')}
                    valueSub={`${entry.accuracy}%`}
                  />
                ))}
              </div>
            </section>
          )}

        </>
      )}
    </div>
  );
};
